import { motion } from "motion/react";
import { ph } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import {
  triggerAnalyticsClick,
  triggerAnalyticsEvent,
} from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingAction, TrackingType } from "@marketing-sites/analytics/types";
import Image from "next/image";
import { FinLogo } from "@marketing-sites/ui/primitives";
import { cn } from "@marketing-sites/utils/cn";

import { useFirstScroll, useScrollToTop, useStagger } from "./hooks";
import { NavRail } from "./nav-rail";
import { Attachment, Avatar, THREAD_SCROLL } from "./primitives";
import type {
  DemoPlacement,
  DemoProduct,
  DemoTicket,
  Industry,
  TicketId,
} from "./tickets";

const INBOX_SCROLL_IDS: Record<Industry, string> = {
  ecommerce: "evt_solutions_ecommerce_demo_inbox_scroll",
  technology: "evt_solutions_technology_demo_inbox_scroll",
  gaming: "evt_solutions_gaming_demo_inbox_scroll",
  fintech: "evt_solutions_fintech_demo_inbox_scroll",
};
const HOME_INBOX_SCROLL_IDS: Record<Industry, string> = {
  ecommerce: "evt_home_ecommerce_demo_inbox_scroll",
  technology: "evt_home_technology_demo_inbox_scroll",
  gaming: "evt_home_gaming_demo_inbox_scroll",
  fintech: "evt_home_fintech_demo_inbox_scroll",
};
const CONVERSATION_IDS: Record<TicketId, string> = {
  ecommerce_product: "btn_solutions_ecommerce_demo_product_conversation",
  ecommerce_orders: "btn_solutions_ecommerce_demo_orders_conversation",
  ecommerce_returns: "btn_solutions_ecommerce_demo_returns_conversation",
  ecommerce_promos: "btn_solutions_ecommerce_demo_promos_conversation",
  technology_api: "btn_solutions_technology_demo_api_conversation",
  technology_billing: "btn_solutions_technology_demo_billing_conversation",
  technology_bug: "btn_solutions_technology_demo_bug_conversation",
  technology_sso: "btn_solutions_technology_demo_sso_conversation",
  gaming_account: "btn_solutions_gaming_demo_account_conversation",
  gaming_purchase: "btn_solutions_gaming_demo_purchase_conversation",
  gaming_crash: "btn_solutions_gaming_demo_crash_conversation",
  gaming_ban: "btn_solutions_gaming_demo_ban_conversation",
  fintech_card: "btn_solutions_fintech_demo_card_conversation",
  fintech_dispute: "btn_solutions_fintech_demo_dispute_conversation",
  fintech_fraud: "btn_solutions_fintech_demo_fraud_conversation",
  fintech_statement: "btn_solutions_fintech_demo_statement_conversation",
};
const HOME_CONVERSATION_IDS: Record<TicketId, string> = {
  ecommerce_product: "btn_home_ecommerce_demo_product_conversation",
  ecommerce_orders: "btn_home_ecommerce_demo_orders_conversation",
  ecommerce_returns: "btn_home_ecommerce_demo_returns_conversation",
  ecommerce_promos: "btn_home_ecommerce_demo_promos_conversation",
  technology_api: "btn_home_technology_demo_api_conversation",
  technology_billing: "btn_home_technology_demo_billing_conversation",
  technology_bug: "btn_home_technology_demo_bug_conversation",
  technology_sso: "btn_home_technology_demo_sso_conversation",
  gaming_account: "btn_home_gaming_demo_account_conversation",
  gaming_purchase: "btn_home_gaming_demo_purchase_conversation",
  gaming_crash: "btn_home_gaming_demo_crash_conversation",
  gaming_ban: "btn_home_gaming_demo_ban_conversation",
  fintech_card: "btn_home_fintech_demo_card_conversation",
  fintech_dispute: "btn_home_fintech_demo_dispute_conversation",
  fintech_fraud: "btn_home_fintech_demo_fraud_conversation",
  fintech_statement: "btn_home_fintech_demo_statement_conversation",
};

function trackConversationClick(
  target: HTMLElement,
  ticketId: TicketId,
  placement: DemoPlacement,
) {
  triggerAnalyticsClick({
    text: ticketId,
    location: getAnalyticsRegion(target) ?? "Unknown",
    analyticsId:
      placement === "home"
        ? HOME_CONVERSATION_IDS[ticketId]
        : CONVERSATION_IDS[ticketId],
  });
}

export function InboxFrame({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex h-full rounded-[20px] border border-black/10 bg-[#eff0eb] py-1.5 pl-0 pr-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.06)]",
        className,
      )}
    >
      <NavRail className="flex" />
      <div className="flex min-w-0 flex-1 overflow-hidden rounded-[14px] border border-black/[0.06]">
        {children}
      </div>
    </div>
  );
}

export function InboxList({
  tickets,
  activeIndex,
  onSelect,
  placement = "solutions",
  className,
}: {
  tickets: DemoTicket[];
  activeIndex: number;
  onSelect: (index: number) => void;
  placement?: DemoPlacement;
  className?: string;
}) {
  const { i18n, t } = useLingui();
  return (
    <div
      className={cn(
        "flex w-full shrink-0 flex-col bg-[#fbfbf9] pb-2 md:w-[220px] md:border-r md:border-black/[0.06] md:pb-0",
        className,
      )}
    >
      <div className="flex h-12 items-center px-4 text-[14px] font-semibold">
        <Trans>Your inbox</Trans>
      </div>
      <div className="flex items-center justify-between px-4 pb-2 text-[11px] font-medium text-black/60">
        <span>
          <Trans>{ph({ count: tickets.length })} Open</Trans>
        </span>
        <span>
          <Trans>Last activity</Trans>
        </span>
      </div>
      <ul className="flex flex-col gap-1 px-2">
        {tickets.map((ticket, index) => {
          const active = index === activeIndex;
          return (
            <li key={ticket.id}>
              <button
                type="button"
                aria-pressed={active}
                onClick={(event) => {
                  trackConversationClick(
                    event.currentTarget,
                    ticket.id,
                    placement,
                  );
                  onSelect(index);
                }}
                className={cn(
                  "flex w-full cursor-pointer gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors",
                  active
                    ? "border-black/10 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                    : "border-transparent hover:bg-black/[0.03]",
                )}
              >
                <Avatar name={ticket.name} className="mt-px" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-[12px] font-semibold">
                      {ticket.name}
                    </span>
                    <span className="shrink-0 text-[11px] text-black/50">
                      {t`${ph({ minutes: ticket.minutesAgo })}m`}
                    </span>
                  </span>
                  <span className="truncate text-[11px] text-black/60">
                    {i18n._(ticket.preview)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function InboxConversation({
  ticket,
  industry,
  placement = "solutions",
  className,
}: {
  ticket: DemoTicket;
  industry: Industry;
  placement?: DemoPlacement;
  className?: string;
}) {
  const { i18n } = useLingui();
  const stagger = useStagger();
  const containerRef = useScrollToTop(ticket.id);
  const onScroll = useFirstScroll(ticket.id, (target) =>
    triggerAnalyticsEvent({
      action: TrackingAction.Event,
      type: TrackingType.Element,
      text: ticket.id,
      analyticsId:
        placement === "home"
          ? HOME_INBOX_SCROLL_IDS[industry]
          : INBOX_SCROLL_IDS[industry],
      location: getAnalyticsRegion(target) ?? "Unknown",
    }),
  );
  return (
    <div
      className={cn(
        "hidden min-w-0 flex-1 flex-col bg-white md:flex",
        className,
      )}
    >
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-black/[0.06] px-5">
        <span className="text-[14px] font-semibold">{ticket.name}</span>
        <span aria-hidden className="flex items-center gap-2 text-[11px]">
          <span className="rounded-full bg-[#1a1a1a] px-2.5 py-1 font-medium text-white">
            <Trans>Close</Trans>
          </span>
          <span className="rounded-full bg-black/[0.05] px-2.5 py-1 font-medium">
            <Trans>Details</Trans>
          </span>
        </span>
      </div>
      <div
        ref={containerRef}
        onScroll={onScroll}
        className={cn("flex min-h-0 flex-1 flex-col px-5 py-4", THREAD_SCROLL)}
      >
        <div className="flex flex-col gap-3">
          {ticket.messages.map((message, index) => (
            <motion.div
              key={`${ticket.id}-${index}`}
              {...stagger(index)}
              className={cn(
                "flex items-end gap-2",
                message.from !== "customer" && "flex-row-reverse",
              )}
            >
              {message.from === "customer" ? (
                <Avatar name={ticket.name} />
              ) : (
                <FinLogo.Symbol className="size-4 shrink-0" />
              )}
              <div
                className={cn(
                  "flex max-w-[78%] flex-col gap-2",
                  message.from === "customer" ? "items-start" : "items-end",
                )}
              >
                {message.attachment && (
                  <Attachment attachment={message.attachment} />
                )}
                <p
                  className={cn(
                    "rounded-xl px-3.5 py-2.5 text-[12.5px] leading-[1.45]",
                    message.from === "customer"
                      ? "border border-black/[0.08] bg-[#fbfbf9]"
                      : "bg-[#eef2f8]",
                  )}
                >
                  {i18n._(message.text)}
                  {message.link && (
                    <span className="block text-[#165fc6] underline">
                      {message.link}
                    </span>
                  )}
                </p>
                {ticket.products && index === ticket.products.afterMessage && (
                  <div className="flex w-full flex-col gap-2">
                    {ticket.products.items.map((product) => (
                      <InboxProductCard key={product.name} product={product} />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          <motion.p
            key={`${ticket.id}-system`}
            {...stagger(ticket.messages.length)}
            className="mt-1 text-right text-[11px] text-black/50"
          >
            {i18n._(ticket.system)}
          </motion.p>
        </div>
      </div>
      <div aria-hidden className="shrink-0 p-3">
        <div className="rounded-xl border border-black/10 px-3 py-2.5 text-[11px] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <span className="font-semibold">
            <Trans>Reply</Trans>
          </span>
          <p className="mt-1.5 text-black/45">
            <Trans>Use ⌘K for shortcuts…</Trans>
          </p>
        </div>
      </div>
    </div>
  );
}

function InboxProductCard({ product }: { product: DemoProduct }) {
  const { i18n } = useLingui();
  return (
    <div className="flex w-full items-center gap-3 rounded-lg border border-[#e9eae6] bg-white p-2.5">
      <span className="relative size-12 shrink-0 overflow-hidden rounded">
        <Image
          src={product.image}
          alt={product.name}
          fill
          sizes="48px"
          className="object-contain"
        />
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[12.5px] font-semibold text-[#165fc6] underline">
          {product.name}
        </span>
        <span className="truncate text-[11px] text-black/55">
          {i18n._(product.price)}
        </span>
        <span className="line-clamp-2 text-[11px] text-black/55">
          {i18n._(product.description)}
        </span>
      </span>
    </div>
  );
}
